/**
 * Report Field Configuration
 * Defines dynamic fields for different report templates
 */

// Field type definitions
export const FIELD_TYPES = {
  SELECT: "select",
  DATE: "date",
  TEXT: "text",
  NUMBER: "number",
  CURRENCY: "currency",
  MULTISELECT: "multiselect",
  CHECKBOX: "checkbox",
  BUTTON: "button",
};

// Field validation rules
export const VALIDATION_RULES = {
  REQUIRED: "required",
  EMAIL: "email",
  MIN_LENGTH: "minLength",
  MAX_LENGTH: "maxLength",
  MIN_VALUE: "minValue",
  MAX_VALUE: "maxValue",
  PATTERN: "pattern",
};

// Report template field configurations
export const REPORT_FIELD_CONFIGS = {
  security_cross_reference: {
    title: "Security Cross Reference Report",
    description: "Generate a comprehensive security cross-reference report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
      {
        id: "security_type",
        label: "Security Type",
        type: FIELD_TYPES.TEXT,
        required: true,
        placeholder: "e.g., Municipal Bond, Corporate Bond",
      },
      {
        id: "security_symbol",
        label: "Security Name/Symbol",
        type: FIELD_TYPES.TEXT,
        required: true,
        placeholder: "e.g., CUSIP or Symbol",
      },
    ],
  },

  portfolio_summary: {
    title: "Portfolio Summary Report",
    description: "Generate a comprehensive portfolio summary report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
      {
        id: "include_performance",
        label: "Include Performance Metrics",
        type: FIELD_TYPES.CHECKBOX,
        required: false,
        defaultValue: true,
      },
      {
        id: "include_allocation",
        label: "Include Asset Allocation",
        type: FIELD_TYPES.CHECKBOX,
        required: false,
        defaultValue: true,
      },
    ],
  },



  custom_report: {
    title: "Custom Report",
    description: "Generate a custom report with specific parameters",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
      {
        id: "report_sections",
        label: "Report Sections",
        type: FIELD_TYPES.MULTISELECT,
        required: false,
        options: [
          { value: "holdings", label: "Holdings" },
          { value: "transactions", label: "Transactions" },
          { value: "performance", label: "Performance" },
          { value: "allocation", label: "Asset Allocation" },
          { value: "risk_metrics", label: "Risk Metrics" },
        ],
        defaultValue: ["holdings", "performance"],
      },
      {
        id: "custom_notes",
        label: "Custom Notes",
        type: FIELD_TYPES.TEXT,
        required: false,
        placeholder: "Enter any additional notes for the report...",
      },
    ],
  },

  asset_allocation: {
    title: "Asset Allocation Report",
    description: "Generate a detailed asset allocation analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
      {
        id: "allocation_level",
        label: "Allocation Level",
        type: FIELD_TYPES.SELECT,
        required: false,
        options: [
          { value: "high_level", label: "High Level (Asset Classes)" },
          { value: "detailed", label: "Detailed (Sectors/Sub-assets)" },
          { value: "both", label: "Both Levels" },
        ],
        defaultValue: "high_level",
      },
      {
        id: "include_benchmark",
        label: "Include Benchmark Comparison",
        type: FIELD_TYPES.CHECKBOX,
        required: false,
        defaultValue: true,
      },
    ],
  },

  fixed_income_distribution: {
    title: "Fixed Income Distribution Report",
    description:
      "Generate a comprehensive fixed income holdings distribution report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  // Add new performance attribution reports

  equity_performance_attribution: {
    title: "Equity Performance Attribution Report",
    description: "Generate a comprehensive equity performance attribution report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  alternative_performance_attribution: {
    title: "Alternative Performance Attribution Report",
    description: "Generate a comprehensive alternative investment performance attribution report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  portfolio_performance_attribution: {
    title: "Portfolio Performance Attribution Report",
    description: "Generate a comprehensive overall portfolio performance attribution report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  exposure_by_sector: {
    title: "Exposure by Sector Report",
    description: "Generate a comprehensive sector exposure analysis report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  // New Equity Reports
  style_factor_decomposition: {
    title: "Style Factor Decomposition Report",
    description: "Generate a style factor decomposition analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  earnings_sensitivity: {
    title: "Earnings Sensitivity Report",
    description: "Generate an earnings sensitivity analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  corporate_actions_tracker: {
    title: "Corporate Actions Tracker Report",
    description: "Generate a corporate actions tracking report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  // New Fixed Income Reports
  credit_migration_watchlist: {
    title: "Credit Migration Watchlist Report",
    description: "Generate a credit migration watchlist analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  liquidity_heatmap: {
    title: "Liquidity Heatmap Report",
    description: "Generate a liquidity heatmap analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  // New Alternative Investment Reports
  tenant_concentration_credit: {
    title: "Tenant Concentration & Credit Quality Report",
    description: "Generate a tenant concentration and credit quality analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  lease_expiry_schedule: {
    title: "Lease Expiry / Rollover Schedule Report",
    description: "Generate a lease expiry and rollover schedule analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  cap_rate_sensitivity: {
    title: "Cap Rate Sensitivity Report",
    description: "Generate a cap rate sensitivity analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  value_creation_bridge: {
    title: "Value Creation Bridge Report",
    description: "Generate a value creation bridge analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  vintage_year_performance: {
    title: "Vintage Year IRR/TVPI Report",
    description: "Generate a vintage year performance analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  exit_pipeline: {
    title: "Exit Pipeline Report",
    description: "Generate an exit pipeline analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  gross_net_exposure: {
    title: "Gross/Net Exposure Report",
    description: "Generate a gross/net exposure analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  stress_test_replay: {
    title: "Stress Test Replay Report",
    description: "Generate a stress test replay analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  crowded_trades_monitor: {
    title: "Crowded Trades Monitor Report",
    description: "Generate a crowded trades monitoring analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  // New General Reports
  roll_yield_attribution: {
    title: "Roll Yield Attribution Report",
    description: "Generate a roll yield attribution analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  inventory_supply_demand: {
    title: "Inventory & Supply/Demand Balance Report",
    description: "Generate an inventory and supply/demand balance analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  energy_transition_exposure: {
    title: "Energy Transition Exposure Report",
    description: "Generate an energy transition exposure analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  regulatory_risk: {
    title: "Regulatory Risk Report",
    description: "Generate a regulatory risk analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  inflation_linkage: {
    title: "Inflation Linkage Report",
    description: "Generate an inflation linkage analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  greenfield_brownfield: {
    title: "Greenfield vs. Brownfield Project Report",
    description: "Generate a greenfield vs. brownfield project analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  carry_spot_return: {
    title: "Carry vs. Spot Return Report",
    description: "Generate a carry vs. spot return analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  hedging_effectiveness: {
    title: "Hedging Effectiveness Report",
    description: "Generate a hedging effectiveness analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
  central_bank_sensitivity: {
    title: "Central Bank Policy Sensitivity Report",
    description: "Generate a central bank policy sensitivity analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  // Fixed Income Reports
  fixed_income_holdings_by_maturity: {
    title: "Fixed Income Holdings by Maturity Report",
    description: "Generate a fixed income holdings by maturity analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  fixed_income_amortization_accretion: {
    title: "Fixed Income Amortization and Accretion Report",
    description: "Generate a fixed income amortization and accretion analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  fixed_income_cash_projection: {
    title: "Fixed Income Cash Projection Report",
    description: "Generate a fixed income cash projection analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  fixed_income_analysis: {
    title: "Fixed Income Analysis Report",
    description: "Generate a comprehensive fixed income analysis",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  // Transaction Reports
  transaction_history: {
    title: "Transaction History Report",
    description: "Generate a transaction history report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  transaction_summary: {
    title: "Transaction Summary Report",
    description: "Generate a transaction summary report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  master_transaction_summary: {
    title: "Master Transaction Summary Report",
    description: "Generate a master transaction summary report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  bond_transactions: {
    title: "Bond Transactions Report",
    description: "Generate a bond transactions report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  purchase_and_sale: {
    title: "Purchase and Sale Report",
    description: "Generate a purchase and sale report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  transfer_transactions: {
    title: "Transfer Transactions Report",
    description: "Generate a transfer transactions report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  trade_blotter: {
    title: "Trade Blotter Report",
    description: "Generate a trade blotter report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  significant_contributions_withdrawals: {
    title: "Significant Contributions/Withdrawals Report",
    description: "Generate a significant contributions and withdrawals report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  // Gains/Losses Reports
  realized_gains_losses: {
    title: "Realized Gains/Losses Report",
    description: "Generate a realized gains and losses report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  unrealized_gains_losses: {
    title: "Unrealized Gains/Losses Report",
    description: "Generate an unrealized gains and losses report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  date_to_date_gain_loss: {
    title: "Date to Date Gain/Loss Report",
    description: "Generate a date to date gain/loss report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  // Income Reports
  income_and_expenses: {
    title: "Income and Expenses Report",
    description: "Generate an income and expenses report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  cash_ledger_running_balance: {
    title: "Cash Ledger with Running Balance Report",
    description: "Generate a cash ledger with running balance report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  // Performance Reports
  performance_by_asset_class: {
    title: "Performance by Asset Class Report",
    description: "Generate a performance by asset class report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_by_security: {
    title: "Performance by Security Report",
    description: "Generate a performance by security report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  individual_account_summary: {
    title: "Individual Account Summary Report",
    description: "Generate an individual account summary report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  portfolio_performance_cross_reference: {
    title: "Portfolio Performance Cross Reference Report",
    description: "Generate a portfolio performance cross reference report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_history: {
    title: "Performance History Report",
    description: "Generate a performance history report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_history_by_asset_class: {
    title: "Performance History by Asset Class Report",
    description: "Generate a performance history by asset class report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_history_by_sector: {
    title: "Performance History by Sector Report",
    description: "Generate a performance history by sector report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_history_detail: {
    title: "Performance History Detail Report",
    description: "Generate a performance history detail report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  asset_reconciliation: {
    title: "Asset Reconciliation Report",
    description: "Generate an asset reconciliation report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  performance_reconciliation: {
    title: "Performance Reconciliation Report",
    description: "Generate a performance reconciliation report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  acb_proof: {
    title: "ACB Proof Report",
    description: "Generate an ACB proof report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  historical_market_value: {
    title: "Historical Market Value Report",
    description: "Generate a historical market value report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },


  // General Reports
  performance_summary_compound: {
    title: "Performance Summary Compound Report",
    description: "Generate a performance summary compound report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  change_in_portfolio_value: {
    title: "Change in Portfolio Value Report",
    description: "Generate a change in portfolio value report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  assets_under_management: {
    title: "Assets Under Management Report",
    description: "Generate an assets under management report (optional accrued interest column)",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  assets_under_management_with_port_code: {
    title: "Assets Under Management with Port Code Report",
    description: "Generate an assets under management with port code report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  portfolio_register: {
    title: "Portfolio Register Report",
    description: "Generate a portfolio register report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  // Venture Capital Reports
  company_kpi_dashboard: {
    title: "Company KPI Dashboard Report",
    description: "Generate a company KPI dashboard report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  capital_deployment_reserves: {
    title: "Capital Deployment & Reserves Report",
    description: "Generate a capital deployment and reserves report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  fund_performance_snapshot: {
    title: "Fund Performance Snapshot Report",
    description: "Generate a fund performance snapshot report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  exit_liquidity_pipeline: {
    title: "Exit & Liquidity Pipeline Report",
    description: "Generate an exit and liquidity pipeline report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },

  concentration_diversification: {
    title: "Concentration & Diversification Report",
    description: "Generate a concentration and diversification report",
    fields: [
      {
        id: "portfolio",
        label: "Portfolio",
        type: FIELD_TYPES.SELECT,
        required: true,
        defaultValue: "main",
        options: [
          { value: "main", label: "Main Portfolio" },
          { value: "growth", label: "Growth Portfolio" },
          { value: "income", label: "Income Portfolio" },
          { value: "balanced", label: "Balanced Portfolio" },
        ],
        description: "Specify who you are generating the report for"
      },
      {
        id: "date",
        label: "As of Date",
        type: FIELD_TYPES.DATE,
        required: true,
        defaultValue: new Date().toISOString().split("T")[0],
        description: "The report is generated with data up to the specified date"
      },
      {
        id: "currency",
        label: "Reporting Currency",
        type: FIELD_TYPES.CURRENCY,
        required: true,
        defaultValue: "USD",
      },
    ],
  },
};

/**
 * Get field configuration for a specific report type
 * @param {string} reportType - The report type
 * @returns {Object} Field configuration
 */
export const getReportFieldConfig = (reportType) => {
  return (
    REPORT_FIELD_CONFIGS[reportType] || REPORT_FIELD_CONFIGS["custom_report"]
  );
};

/**
 * Validate field values against configuration
 * @param {Object} fieldConfig - Field configuration
 * @param {Object} values - Field values
 * @returns {Object} Validation result
 */
export const validateFieldValues = (fieldConfig, values) => {
  const errors = {};

  fieldConfig.fields.forEach((field) => {
    const value = values[field.id];

    // Check required fields
    if (field.required && (!value || value === "")) {
      errors[field.id] = `${field.label} is required`;
      return;
    }

    // Skip validation for empty optional fields
    if (!field.required && (!value || value === "")) {
      return;
    }

    // Type-specific validation
    switch (field.type) {
      case FIELD_TYPES.DATE:
        if (value && isNaN(new Date(value).getTime())) {
          errors[field.id] = `${field.label} must be a valid date`;
        }
        break;

      case FIELD_TYPES.NUMBER:
        if (value && isNaN(Number(value))) {
          errors[field.id] = `${field.label} must be a valid number`;
        }
        break;

      case FIELD_TYPES.EMAIL:
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[field.id] = `${field.label} must be a valid email`;
        }
        break;
      default:
        break;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Get default values for a report type
 * @param {string} reportType - The report type
 * @returns {Object} Default values
 */
export const getDefaultValues = (reportType) => {
  const config = getReportFieldConfig(reportType);
  const defaults = {};

  config.fields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      defaults[field.id] = field.defaultValue;
    }
  });

  return defaults;
};

/**
 * Map template names to report types
 * @param {string} templateName - The template name
 * @returns {string} The report type
 */
export const getReportTypeFromTemplateName = (templateName) => {
  const templateMap = {
    // Original reports
    "Security Cross Reference": "security_cross_reference",
    "Portfolio Summary": "portfolio_summary",
    "Portfolio Performance Summary": "portfolio_performance_summary",
    "Fixed Income Distribution": "fixed_income_distribution",
    "Fixed Income Holdings by Maturity": "fixed_income_holdings_by_maturity",
    "Custom Report": "custom_report",
    "Portfolio Diversification": "asset_allocation",
    
    // Performance Attribution reports
    "Portfolio Performance Attribution": "portfolio_performance_attribution",
    "Equity Performance Attribution": "equity_performance_attribution",
    "Alternative Performance Attribution": "alternative_performance_attribution",
    
    // Fixed Income reports
    "Credit Migration Watchlist": "credit_migration_watchlist",
    "Liquidity Heatmap": "liquidity_heatmap",
    "Amortization and Accretion": "fixed_income_amortization_accretion",
    "Fixed Income Cash Projection": "fixed_income_cash_projection",
    "Fixed Income Portfolio": "fixed_income_analysis",
    
    // Equity reports
    "Style Factor Decomposition": "style_factor_decomposition",
    "Earnings Sensitivity": "earnings_sensitivity",
    "Corporate Actions Tracker": "corporate_actions_tracker",
    
    // Real Estate reports
    "Tenant Concentration & Credit Quality": "tenant_concentration_credit",
    "Lease Expiry / Rollover Schedule": "lease_expiry_schedule",
    "Cap Rate Sensitivity": "cap_rate_sensitivity",
    
    // Private Equity reports
    "Value Creation Bridge": "value_creation_bridge",
    "Vintage Year IRR/TVPI": "vintage_year_performance",
    "Exit Pipeline": "exit_pipeline",
    
    // Venture Capital reports
    "Company KPI Dashboard": "company_kpi_dashboard",
    "Capital Deployment & Reserves": "capital_deployment_reserves",
    "Fund Performance Snapshot": "fund_performance_snapshot",
    "Exit & Liquidity Pipeline": "exit_liquidity_pipeline",
    "Concentration & Diversification": "concentration_diversification",
    
    // Hedge Fund reports
    "Gross/Net Exposure": "gross_net_exposure",
    "Stress Test Replay": "stress_test_replay",
    "Crowded Trades Monitor": "crowded_trades_monitor",
    
    // General reports
    "Exposure by Sector": "exposure_by_sector",
    "Performance Summary Compound": "performance_summary_compound",
    "Change in Portfolio Value": "change_in_portfolio_value",
    "Assets Under Management": "assets_under_management",
    "Master Cash Balances": "cash_balances",
    "Billing Statements": "billing",
    
    // Transaction reports
    "Transaction Summary": "transaction_history",
    "Summary by Transaction Type": "transaction_summary",
    "Master Transaction Summary": "master_transaction_summary",
    "Portfolio Register with Close Date and Index1": "portfolio_register",
    "Trade Blotter Report": "trade_blotter",
    "Assets Under Mgmt with Port Code": "assets_under_management_with_port_code",
    "Bond Transactions": "bond_transactions",
    "Purchase and Sale": "purchase_and_sale",
    "Transfer Transactions": "transfer_transactions",
    "Significant Contributions/Withdrawals": "significant_contributions_withdrawals",
    "Funds In and Out": "funds_in_and_out",
    
    // Gains/Losses reports
    "Realized Gains/Losses": "realized_gains_losses",
    "Unrealized Gains/Losses": "unrealized_gains_losses",
    "Date to Date Gain/Loss": "date_to_date_gain_loss",
    
    // Income reports
    "Income and Expenses": "income_and_expenses",
    "Cash Ledger with Running Balance": "cash_ledger_running_balance",
    
    // Performance reports
    "Performance by Asset Class": "performance_by_asset_class",
    "Performance by Security": "performance_by_security",
    "Individual Account Summary": "individual_account_summary",
    "Portfolio Performance Cross Reference": "portfolio_performance_cross_reference",
    "Performance History": "performance_history",
    "Performance History by Asset Class": "performance_history_by_asset_class",
    "Performance History by Sector": "performance_history_by_sector",
    "Performance History Detail": "performance_history_detail",
    "Asset Reconciliation": "asset_reconciliation",
    "Performance Reconciliation": "performance_reconciliation",
    "ACB Proof": "acb_proof",
    "Historical Market Value": "historical_market_value",
    
    // Commodities reports
    "Roll Yield Attribution": "roll_yield_attribution",
    "Inventory & Supply/Demand Balance": "inventory_supply_demand",
    "Energy Transition Exposure": "energy_transition_exposure",
    
    // Infrastructure reports
    "Regulatory Risk": "regulatory_risk",
    "Inflation Linkage": "inflation_linkage",
    "Greenfield vs. Brownfield Project": "greenfield_brownfield",
    
    // Currency/FX reports
    "Carry vs. Spot Return": "carry_spot_return",
    "Hedging Effectiveness": "hedging_effectiveness",
    "Central Bank Policy Sensitivity": "central_bank_sensitivity",
  };
  return templateMap[templateName] || "custom_report";
};
